import type { OpenAPISchemaProperty } from '../types/types';

/**
 * Formats an OpenAPI schema into a TypeScript-style structure for display
 * @param schema - The OpenAPI schema to format
 * @param t - Translation function for internationalized strings
 * @returns Formatted string representation of the schema
 */
export const formatSchemaForDisplay = (
  schema: OpenAPISchemaProperty | undefined,
  t: (key: string) => string,
): string => {
  if (!schema) {
    return t('mdx:openapi:no-schema-available');
  }

  const formatProperty = (prop: OpenAPISchemaProperty, indent: number = 0): string => {
    const indentStr = '  '.repeat(indent);
    let result = '';

    if (prop.type === 'object' && prop.properties) {
      result += `${indentStr}{\n`;
      Object.entries(prop.properties).forEach(([key, value], index, array) => {
        const isRequired = prop.required?.includes(key);
        const requiredMarker = isRequired ? ' (required)' : '';
        result += `${indentStr}  "${key}"${requiredMarker}: `;

        if (value.type === 'object' && value.properties) {
          result += '\n' + formatProperty(value, indent + 1);
        } else if (value.type === 'array' && value.items) {
          result += `array[\n`;
          result += formatProperty(value.items, indent + 2);
          result += `${indentStr}  ]`;
        } else {
          result += `${value.type || 'any'}`;
          if (value.description) {
            result += ` // ${value.description}`;
          }
        }

        if (index < array.length - 1) {
          result += ',';
        }
        result += '\n';
      });
      result += `${indentStr}}`;
    } else if (prop.type === 'array' && prop.items) {
      result += `${indentStr}array[\n`;
      result += formatProperty(prop.items, indent + 1);
      result += `\n${indentStr}]`;
    } else {
      result += `${indentStr}${prop.type || 'any'}`;
      if (prop.description) {
        result += ` // ${prop.description}`;
      }
    }

    return result;
  };

  return formatProperty(schema);
};
